import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import './ContactForm.css';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const subjects = [
    'Question générale',
    'Partenariat',
    'Support technique',
    'Collaboration',
    'Autre'
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    
    if (!formData.subject) {
      newErrors.subject = 'Le sujet est requis';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };

  if (isSubmitted) {
    return (
      <Box className="contact-form-success">
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Message envoyé avec succès !
          </Typography>
          <Typography variant="body2">
            Nous avons bien reçu votre message et vous répondrons dans les plus brefs délais.
            Temps de réponse estimé : 24h.
          </Typography>
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => setIsSubmitted(false)}
          className="new-message-btn"
        >
          Envoyer un nouveau message
        </Button>
      </Box>
    );
  }

  return (
    <Box className="contact-form">
      <Typography variant="h4" component="h2" className="form-title" gutterBottom>
        Envoyez-nous un message
      </Typography>
      
      <Typography variant="body1" className="form-subtitle" sx={{ mb: 3 }}>
        Remplissez le formulaire ci-dessous et nous vous répondrons rapidement.
      </Typography>

      <form onSubmit={handleSubmit} className="contact-form-container">
        <TextField
          fullWidth
          label="Nom complet"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
          required
          className="form-field"
          sx={{ mb: 3 }}
        />

        <TextField
          fullWidth
          label="Adresse email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email}
          required
          className="form-field"
          sx={{ mb: 3 }}
        />

        <FormControl fullWidth sx={{ mb: 3 }} error={!!errors.subject}>
          <InputLabel>Sujet</InputLabel>
          <Select
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            label="Sujet"
            required
          >
            {subjects.map((subject) => (
              <MenuItem key={subject} value={subject}>
                {subject}
              </MenuItem>
            ))}
          </Select>
          {errors.subject && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
              {errors.subject}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          label="Message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          error={!!errors.message}
          helperText={errors.message}
          required
          multiline
          rows={6}
          className="form-field"
          sx={{ mb: 3 }}
        />

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isLoading}
          startIcon={<SendIcon />}
          className="submit-btn"
          fullWidth
        >
          {isLoading ? 'Envoi en cours...' : 'Envoyer le message'}
        </Button>
      </form>
    </Box>
  );
};

export default ContactForm;